/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Chip, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ContentShell from "components/ContentShell";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { SimpleTextInput } from "components/FormTextInput";
import {
  createEOSRewardConfig,
  getEOSRewardConfigInfo,
  updateEOSRewardConfig,
} from "services/eosRewardConfigServices";
import { getPlayerPacks, packInfo } from "services/packServices";
import { eosRewardConfigValidation } from "utils/validation";
import SearchableInput from "components/SearchableInput";
import useStyles from "styles/banner/bannerEditor";
import { defaultPageNo, defaultPageSize } from "utils/constants";

const DEFAULT_CONFIG_DETAILS = {
  tier: 0,
  startPoints: 0,
  endPoints: 0,
  rewardCoins: 0,
  rewards: [
    {
      rewardPack: "",
      rewardPackCount: 1,
    },
  ],
};

// ─── Section Card ───────────────────────────────────────────
const SectionCard = ({
  step,
  icon,
  title,
  subtitle,
  accent = "#1976d2",
  children,
  isComplete,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: string;
  children: React.ReactNode;
  isComplete?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: isComplete ? `${accent}60` : "divider",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      transition: "border-color 0.3s, box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}12` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}40`,
        }}
      >
        {step}
      </Box>
      <Box sx={{ color: accent, display: "flex", fontSize: 20 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.primary"
          sx={{ lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {isComplete !== undefined && (
        <Tooltip
          title={isComplete ? "Section complete" : "Fill in required fields"}
        >
          <Box
            sx={{ display: "flex", color: isComplete ? "#4caf50" : "#bdbdbd" }}
          >
            {isComplete ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" />
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const EOSRewardConfigEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const id = location.pathname.split("/").slice(-1)[0];
  const isCreateMode = id === "create";

  const [loading, setLoading] = useState(false);
  const [configDetails, setConfigDetails] = useState(DEFAULT_CONFIG_DETAILS);
  const [rewardDetails, setRewardDetails] = React.useState({
    rewardOptions: [],
    rewards: [],
  });

  const [searchPagination] = useState({
    pageNo: defaultPageNo,
    pageSize: defaultPageSize,
    hasMore: false,
  });

  const transformRewardPacks = (packs = []) => {
    return packs.map((pack) => ({
      value: pack._id, // Some components expect 'value' instead of 'id'
      id: pack._id, // Keep id for backward compatibility
      label: `${pack.name || "Unnamed Pack"} - ${pack.type || "No Type"} - ${
        pack._id
      }`,
    }));
  };

  const onSearch = (searchValue = "") => {
    if (searchValue !== "")
      fetchRewardPacks({
        search: searchValue,
        skipPagination: true,
        ...searchPagination,
      });
  };

  const fetchRewardPacks = async (filters = {}, init = false) => {
    setRewardDetails((st) => ({ ...st, isFetched: false }));
    try {
      const response = await getPlayerPacks(filters);
      const options = transformRewardPacks(response.items);
      setRewardDetails((st) => {
        let data = { ...st };
        if (init) data.rewards = response.items;
        return {
          ...data,
          rewardOptions: options,
        };
      });

      return options;
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const cancel = () => navigate(-1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await eosRewardConfigValidation.validate(configDetails, {
        abortEarly: false,
      });
      const payload = {
        tier: Number(configDetails.tier),
        startPoints: Number(configDetails.startPoints),
        endPoints: Number(configDetails.endPoints),
        rewardCoins: Number(configDetails.rewardCoins),
        rewards: configDetails.rewards.map((reward) => ({
          rewardPack: reward.rewardPack,
          rewardPackCount: Number(reward.rewardPackCount),
        })),
      };
      const hasValidRewards = payload.rewards.some(
        (reward) => reward.rewardPack
      );
      if (!hasValidRewards) {
        throw new Error("At least one reward must have a pack selected");
      }

      const response = isCreateMode
        ? await createEOSRewardConfig(payload)
        : await updateEOSRewardConfig(payload, id);

      showSnackbar(response.message, "success");
      navigate(configData.routes.eosRewardConfig.eosRewardConfig);
    } catch (error) {
      // Yup with abortEarly:false collects every failure on `errors`.
      const messages = error?.errors?.length ? error.errors : [error.message];
      messages.forEach((message: string) => showSnackbar(message, "error"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setConfigDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleRewardChange = (index, e) => {
    const { name, value } = e.target || {};

    setConfigDetails((prev) => {
      const updatedRewards = [...prev.rewards];

      if (name === "rewardPack") {
        const packId = value?.id || value?.value;
        updatedRewards[index] = {
          ...updatedRewards[index],
          rewardPack: packId,
        };
      } else {
        updatedRewards[index] = {
          ...updatedRewards[index],
          [name]: value,
        };
      }

      return {
        ...prev,
        rewards: updatedRewards,
      };
    });
  };

  const addReward = () => {
    setConfigDetails((prev) => ({
      ...prev,
      rewards: [
        ...prev.rewards,
        {
          rewardPack: "",
          rewardPackCount: 1,
        },
      ],
    }));
  };

  const removeReward = (index: any) => {
    setConfigDetails((prev) => {
      return {
        ...prev,
        rewards: [
          ...prev.rewards.slice(0, index),
          ...prev.rewards.slice(index + 1),
        ],
      };
    });
  };

  const fetchConfigInfo = async (rewardOP = []) => {
    try {
      setLoading(true);
      const resp = await getEOSRewardConfigInfo(id);
      // Configs created before PBF coins existed return null for rewardCoins.
      setConfigDetails({ ...resp.data, rewardCoins: resp.data?.rewardCoins ?? 0 });

      const requests = [];

      for (let reward of resp?.data?.rewards || []) {
        const data = rewardOP.find((el) => el.id === reward.rewardPack);
        if (!data) requests.push(packInfo(reward.rewardPack));
      }

      if (!requests.length) return;

      const results = await Promise.allSettled(requests);
      const newRewards = [];

      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value.status) {
          newRewards.push(result.value.data);
        } else if (result.status === "rejected") {
          showSnackbar(
            result.reason?.message ?? "Could not find the reward pack",
            "error"
          );
        }
      });

      setRewardDetails((st) => ({
        ...st,
        rewardOptions: [
          ...st.rewardOptions,
          ...transformRewardPacks(newRewards),
        ],
        rewards: [...st.rewards, ...newRewards],
      }));
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const getData = async () => {
      const data = await fetchRewardPacks(searchPagination, true);
      id !== "create" && (await fetchConfigInfo(data));
    };
    getData();
  }, []);

  const startPoints = Number(configDetails.startPoints);
  const endPoints = Number(configDetails.endPoints);
  const rewardCoins = Number(configDetails.rewardCoins);
  const isRangeValid = endPoints > startPoints;

  const isTierComplete =
    `${configDetails.tier}` !== "" && Number(configDetails.tier) >= 0 && isRangeValid;
  const isCoinsComplete =
    `${configDetails.rewardCoins}` !== "" &&
    Number.isFinite(rewardCoins) &&
    rewardCoins >= 0;
  const isPacksComplete =
    configDetails.rewards.length > 0 &&
    configDetails.rewards.every(
      (reward) => reward.rewardPack && Number(reward.rewardPackCount) > 0
    );

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${isCreateMode ? "Create" : "Edit"} EOS Reward Config`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button variant="outlined" color="inherit" onClick={cancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            {isCreateMode ? "Save" : "Update"}
          </Button>
        </Box>
      }
    >
      <Box sx={{ maxWidth: 960, pb: 4 }}>
        {/* ── 1. Tier & Season Points ──────────────────────── */}
        <SectionCard
          step={1}
          icon={<EmojiEventsOutlinedIcon fontSize="small" />}
          title="Tier & Season Points"
          subtitle="Which players this tier applies to at the End of Season reset"
          accent="#1565c0"
          isComplete={isTierComplete}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Tier"
                name="tier"
                value={configDetails.tier}
                onChange={handleChange}
                type="number"
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Start Points"
                name="startPoints"
                value={configDetails.startPoints}
                onChange={handleChange}
                type="number"
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                label="End Points"
                name="endPoints"
                value={configDetails.endPoints}
                onChange={handleChange}
                type="number"
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            {isRangeValid ? (
              <Chip
                size="small"
                label={`Matches ${startPoints} ≤ season points < ${endPoints}`}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: "#e3f2fd",
                  color: "#1565c0",
                  border: "1px solid #bbdefb",
                }}
              />
            ) : (
              <Chip
                size="small"
                label="End Points must be greater than Start Points"
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: "#fff3e0",
                  color: "#e65100",
                  border: "1px solid #ffe0b2",
                }}
              />
            )}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5, lineHeight: 1.6 }}
          >
            Start Points is inclusive and End Points is exclusive — a player on
            exactly the End Points value falls into the next tier. Players whose
            season points fall outside every configured tier receive no coins and
            no packs, so make sure the tiers together cover the full points range.
          </Typography>
        </SectionCard>

        {/* ── 2. PBF Coins ─────────────────────────────────── */}
        <SectionCard
          step={2}
          icon={<MonetizationOnOutlinedIcon fontSize="small" />}
          title="PBF Coins"
          subtitle="Fixed coin amount awarded to every player in this tier"
          accent="#ef6c00"
          isComplete={isCoinsComplete}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box className={styles.field}>
              <SimpleTextInput
                label="PBF Coins"
                name="rewardCoins"
                value={configDetails.rewardCoins}
                onChange={handleChange}
                type="number"
              />
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            Credited automatically when the End of Season reset runs, and added
            on top of the player&apos;s existing balance, which carries over from
            the previous season. A tier left at 0 awards no coins.
          </Alert>
        </SectionCard>

        {/* ── 3. Reward Packs ──────────────────────────────── */}
        <SectionCard
          step={3}
          icon={<Inventory2OutlinedIcon fontSize="small" />}
          title="Reward Packs"
          subtitle="Packs this tier grants — claimed by the player from the End of Season pop-up"
          accent="#6a1b9a"
          isComplete={isPacksComplete}
        >
          {configDetails.rewards.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              At least one reward pack is required.
            </Alert>
          )}

          {configDetails.rewards.map((reward, index) => {
            const _rewardPack = rewardDetails.rewardOptions.find(
              (_) => _.id === reward.rewardPack
            );
            const selected = _rewardPack || {
              label: reward.rewardPack,
              id: reward.rewardPack,
              value: reward.rewardPack,
            };
            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: reward.rewardPack ? "#6a1b9a30" : "divider",
                  bgcolor: reward.rewardPack ? "#6a1b9a08" : "transparent",
                  transition: "all 0.2s",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #6a1b9a, #9c27b0)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      boxShadow: "0 2px 6px #6a1b9a40",
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ flex: 1 }}
                  >
                    {`Pack #${index + 1}`}
                  </Typography>
                  {reward.rewardPack && Number(reward.rewardPackCount) > 0 && (
                    <CheckCircleIcon
                      sx={{ fontSize: 18, color: "#4caf50", mr: 0.5 }}
                    />
                  )}
                  <Tooltip
                    title={
                      configDetails.rewards.length === 1
                        ? "At least one reward pack is required"
                        : "Remove this pack"
                    }
                  >
                    <span>
                      <IconButton
                        color="error"
                        size="small"
                        disabled={configDetails.rewards.length === 1}
                        onClick={() => removeReward(index)}
                      >
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box sx={{ minWidth: 320, flex: 1 }}>
                    <SearchableInput
                      label="Reward Pack"
                      name="rewardPack"
                      selected={selected}
                      options={rewardDetails.rewardOptions}
                      onInputChange={onSearch}
                      onOptionSelect={(e) => handleRewardChange(index, e)}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                    />
                  </Box>
                  <Box className={styles.smallField}>
                    <SimpleTextInput
                      label="Count"
                      name="rewardPackCount"
                      value={reward.rewardPackCount}
                      onChange={(e) => handleRewardChange(index, e)}
                      type="number"
                    />
                  </Box>
                </Box>
              </Paper>
            );
          })}

          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={addReward}
            sx={{
              mt: 1,
              borderColor: "#6a1b9a",
              color: "#6a1b9a",
              "&:hover": { borderColor: "#6a1b9a", bgcolor: "#6a1b9a0a" },
            }}
          >
            Add Pack
          </Button>
        </SectionCard>
      </Box>
    </ContentShell>
  );
};

export default EOSRewardConfigEditor;
